// Settings → Outreach: keeps the two previews in step with the form.
//
// The email preview follows the signature and button toggles. The text
// preview recomposes the sample with the sign-off on every keystroke, the
// way campaigns.ComposeSMS composes a real text, and counts what it
// costs. The first render of each comes from the server.
//
// Targets: [data-outreach-form].

import { collapseSignOff, composeSMS, smsEncoding } from "./sms_encoding.js";
import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

export function initOutreachSettings(root = document) {
  root.querySelectorAll("[data-outreach-form]").forEach((form) => {
    if (!firstBind(form)) return;

    const signature = form.elements.namedItem("show_email_signature");
    const button = form.elements.namedItem("show_book_meeting_button");
    const signOff = form.querySelector("[data-outreach-sign-off]");
    const signOffCost = form.querySelector("[data-outreach-sign-off-cost]");
    const textPreview = form.querySelector("[data-outreach-text-preview]");
    const textCost = form.querySelector("[data-outreach-text-cost]");
    const signaturePreview = form.querySelector("[data-outreach-signature]");
    const buttonPreview = form.querySelector("[data-outreach-button]");
    const rulePreview = form.querySelector("[data-outreach-rule]");

    const footer = form.dataset.footerText || "";
    const sample = form.dataset.sampleText || "";
    const maxLength = Number(form.dataset.maxLength);
    const { encodedUnits, singleSize, segmentsFor } = smsEncoding(form.dataset);

    const syncEmail = () => {
      signaturePreview.hidden = !signature.checked;
      buttonPreview.hidden = !button.checked;
      rulePreview.hidden = !signature.checked && !button.checked;
    };

    // Mirrors settings.signOffCost and settings.sampleCost.
    const syncText = () => {
      const value = collapseSignOff(signOff.value);
      const length = [...value].length;
      const signed = value
        ? encodedUnits(`\n${value}`)
        : { units: 0, gsm: true };

      let cost = `${length}/${maxLength} characters`;
      if (value) cost += ` · adds ${signed.units} to every text`;
      if (!signed.gsm)
        cost += ` and drops each message to ${singleSize(false)} characters`;
      signOffCost.textContent = cost;
      // Past the cap the save refuses, which outranks the encoding warning.
      const over = length > maxLength;
      signOffCost.classList.toggle("text-red-600", over);
      signOffCost.classList.toggle("text-amber-700", !over && !signed.gsm);
      signOffCost.classList.toggle("text-ink-500", !over && signed.gsm);

      const text = composeSMS(sample, value, footer);
      const { units, gsm } = encodedUnits(text);
      const count = segmentsFor(units, gsm);
      textPreview.textContent = text;
      textCost.textContent = `${units} characters · ${count === 1 ? "1 message" : `${count} messages`}`;
    };

    signature.addEventListener("change", syncEmail);
    button.addEventListener("change", syncEmail);
    signOff.addEventListener("input", syncText);
    // A reset restores the rendered values after this event, so sync on
    // the next frame.
    form.addEventListener("reset", () =>
      requestAnimationFrame(() => {
        syncEmail();
        syncText();
      }),
    );
  });
}
